// Feedback #503 — per-matter attorney rates card.
// Renders on MatterDetail's billing tab.  Mirrors the shape of Team.js's
// RateHistorySection but scoped to matter_rates (user_id=NULL for the
// matter-wide default; user_id=X for per-attorney overrides).
//
// Rate precedence (from api/src/lib/attorneyRates.js:26):
//   1. matter_rates row for this (matter, user) as-of the entry's date
//   2. matter_rates row for this (matter, NULL user) as-of that date
//   3. attorney_rate_history for the user
//   4. users.hourly_rate
//   5. 0
//
// Admin (or can_edit_matters) can add / delete rows.  Effective_date required —
// tie-breaks precedence for time entries logged retroactively.

import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import api from '../services/api'
import { useAsk } from '../context/PromptProvider'
import { useToast } from '../context/ToastContext'
import { parseDateOnly } from '../utils/format'

export default function MatterRatesCard ({ matterId, canEdit }) {
  const [rates, setRates] = useState([])
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ rate: '', effective_date: format(new Date(), 'yyyy-MM-dd'), user_id: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const ask = useAsk()

  async function load () {
    setLoading(true)
    try {
      const r = await api.get(`/api/matters/${matterId}/rates`)
      setRates(r.data || [])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load rate history')
    } finally {
      setLoading(false)
    }
  }
  // Team list for the per-attorney picker (only when adding).
  async function loadTeam () {
    if (team.length > 0) return
    try {
      const r = await api.get('/api/team')
      setTeam((r.data || []).filter(u => u.is_active))
    } catch { /* graceful degrade — picker just shows "Matter-wide default" */ }
  }

  useEffect(() => { load() /* eslint-disable-next-line */ }, [matterId])

  async function addRate () {
    if (!form.rate || parseFloat(form.rate) < 0) { toast.error('Rate must be a non-negative number'); return }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effective_date)) { toast.error('Effective date must be YYYY-MM-DD'); return }
    setSaving(true)
    try {
      await api.post(`/api/matters/${matterId}/rates`, {
        rate: parseFloat(form.rate),
        effective_date: form.effective_date,
        user_id: form.user_id || null,
        notes: form.notes || null,
      })
      toast.success('Rate added')
      setForm({ rate: '', effective_date: format(new Date(), 'yyyy-MM-dd'), user_id: '', notes: '' })
      setAdding(false)
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add rate')
    } finally {
      setSaving(false)
    }
  }

  async function removeRate (row) {
    const target = row.user_name ? `${row.user_name}'s override` : 'the matter-wide default'
    const ok = await ask({
      title: 'Remove this rate?',
      message: `This will remove ${target} of $${parseFloat(row.rate).toFixed(2)} effective ${format(parseDateOnly(row.effective_date), 'MMM d, yyyy')}.  Time entries already logged against this rate keep their historical value.`,
      confirmLabel: 'Remove',
      tone: 'danger',
    })
    if (!ok) return
    try {
      await api.delete(`/api/matters/${matterId}/rates/${row.id}`)
      toast.success('Rate removed')
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove rate')
    }
  }

  // Group per-attorney overrides separately from the matter-wide default.
  const wideDefaults = rates.filter(r => r.user_id == null)
  const overrides = rates.filter(r => r.user_id != null)

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-4">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">💵 Attorney Rates for this Matter</h3>
          <p className="text-xs text-gray-500 mt-1">
            Precedence: attorney-specific override → matter-wide default → attorney's firm rate history → default hourly rate.
          </p>
        </div>
        {canEdit && !adding && (
          <button onClick={() => { setAdding(true); loadTeam() }}
            className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            + Add rate
          </button>
        )}
      </div>

      {adding && (
        <div className="px-6 py-4 border-b border-gray-100 bg-primary-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rate ($ / hr)</label>
              <input type="number" step="0.01" min="0" value={form.rate}
                onChange={e => setForm({...form, rate: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="450.00" autoFocus />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Effective date</label>
              <input type="date" value={form.effective_date}
                onChange={e => setForm({...form, effective_date: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Applies to</label>
              <select value={form.user_id}
                onChange={e => setForm({...form, user_id: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Matter-wide default (everyone)</option>
                {team.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
              <input type="text" value={form.notes}
                onChange={e => setForm({...form, notes: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. Retainer agreement rate" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setAdding(false); setForm({ rate: '', effective_date: format(new Date(), 'yyyy-MM-dd'), user_id: '', notes: '' }) }}
              className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg">Cancel</button>
            <button onClick={addRate} disabled={saving}
              className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save rate'}
            </button>
          </div>
        </div>
      )}

      <div className="p-6">
        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {!loading && rates.length === 0 && (
          <p className="text-sm text-gray-500 italic">
            No matter-specific rates yet.  Time entries fall back to each attorney's firm-wide rate.
            {canEdit && ' Click "+ Add rate" to override.'}
          </p>
        )}
        {!loading && wideDefaults.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Matter-wide default</h4>
            {/* Both rate tables scroll in their own box, so in a narrow
                pop-out or phone a long attorney name or note scrolls the
                table here instead of pushing the page (and Remove) past the
                window edge. */}
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2">Rate</th>
                  <th className="text-left py-2">Effective</th>
                  <th className="text-left py-2">Notes</th>
                  {canEdit && <th className="text-right py-2 w-20">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {wideDefaults.map(r => (
                  <tr key={r.id}>
                    <td className="py-2 font-semibold">${parseFloat(r.rate).toFixed(2)}</td>
                    <td className="py-2 text-gray-600">{format(parseDateOnly(r.effective_date), 'MMM d, yyyy')}</td>
                    <td className="py-2 text-gray-600 text-xs">{r.notes || '—'}</td>
                    {canEdit && (
                      <td className="py-2 text-right">
                        <button onClick={() => removeRate(r)} className="text-xs text-red-600 hover:underline">Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
        {!loading && overrides.length > 0 && (
          <div>
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Per-attorney overrides</h4>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2">Attorney</th>
                  <th className="text-left py-2">Rate</th>
                  <th className="text-left py-2">Effective</th>
                  <th className="text-left py-2">Notes</th>
                  {canEdit && <th className="text-right py-2 w-20">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {overrides.map(r => (
                  <tr key={r.id}>
                    <td className="py-2">{r.user_name || r.user_email || `User #${r.user_id}`}</td>
                    <td className="py-2 font-semibold">${parseFloat(r.rate).toFixed(2)}</td>
                    <td className="py-2 text-gray-600">{format(parseDateOnly(r.effective_date), 'MMM d, yyyy')}</td>
                    <td className="py-2 text-gray-600 text-xs">{r.notes || '—'}</td>
                    {canEdit && (
                      <td className="py-2 text-right">
                        <button onClick={() => removeRate(r)} className="text-xs text-red-600 hover:underline">Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
